/**
 * Authentication utilities using Cognito REST API directly (no Amplify dependency)
 */

import type { AuthTokens, UserProfile } from '@shared/types/index';
import { withBasePath } from './basePath';

const AUTH_STORAGE_KEY = 've_auth_tokens';
const PROFILE_STORAGE_KEY = 've_user_profile';

export const config = {
  userPoolId: import.meta.env.VITE_USER_POOL_ID || '',
  userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID || '',
  region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
  apiUrl: (import.meta.env.VITE_API_URL || '').replace(/\/$/, ''),
  mockMode: import.meta.env.VITE_MOCK_MODE === 'true' || import.meta.env.VITE_USER_POOL_ID === 'us-east-1_mock',
};

// Cognito endpoint for the user pool's region
const COGNITO_ENDPOINT = `https://cognito-idp.${config.region}.amazonaws.com/`;

/**
 * Initialize auth and check if user is authenticated.
 * If a ?token= query param is present (from the mobile app SSO), store it directly.
 */
export async function initAuth(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const ssoToken = params.get('token');

  if (ssoToken) {
    // Strip token from URL immediately — don't leave a JWT in the address bar or history
    window.history.replaceState({}, '', window.location.pathname);

    const ssoResult = await loginWithMobileToken(ssoToken);
    if (ssoResult) return true;
    // Falls through to show login page if token is invalid/expired
  }

  const tokens = getStoredTokens();

  if (!tokens) {
    return false;
  }

  // Check if token is expired
  if (isTokenExpired(tokens)) {
    console.log('Token expired, attempting refresh...');
    try {
      await refreshTokens();
      return true;
    } catch (error) {
      console.error('Failed to refresh tokens:', error);
      clearAuth();
      return false;
    }
  }

  return true;
}

/**
 * Store a mobile app access token directly (SSO flow).
 * The mobile app and portal share the same Cognito User Pool.
 * The token is already valid for ValleyEats-DriverAPI — no exchange needed.
 */
export async function loginWithMobileToken(accessToken: string): Promise<boolean> {
  try {
    // Decode JWT payload to extract email/sub (no signature verification needed here —
    // the API will reject invalid tokens on first call)
    const payload = decodeJwtPayload(accessToken);
    if (!payload) return false;

    // Check if token is already expired
    if (payload.exp && Date.now() >= payload.exp * 1000) {
      console.warn('SSO token is already expired');
      return false;
    }

    const email = payload.email || payload['cognito:username'] || payload.username || '';
    const sub = payload.sub || '';

    if (!email) return false;

    const tokens: AuthTokens = {
      accessToken,
      idToken: accessToken, // SSO supplies a single JWT; may be an ID token or access token
      refreshToken: '',     // No refresh token from SSO — session expires with access token
      expiresIn: payload.exp ? payload.exp - Math.floor(Date.now() / 1000) : 3600,
    };

    const profile: UserProfile = {
      sub,
      email,
      email_verified: true,
      'custom:driverId': email,
    };

    storeTokens(tokens);
    storeProfile(profile);

    return true;
  } catch (error) {
    console.error('SSO login error:', error);
    return false;
  }
}

/**
 * Login with email and password using Cognito directly
 */
export async function login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
  // MOCK MODE: Skip API call for local development without backend
  if (config.mockMode) {
    console.log('🎭 Mock Mode: Bypassing API login');

    await new Promise(resolve => setTimeout(resolve, 500));

    const mockTokens: AuthTokens = {
      accessToken: 'mock_access_token',
      idToken: 'mock_id_token',
      refreshToken: 'mock_refresh_token',
      expiresIn: 3600,
    };

    const mockProfile: UserProfile = {
      sub: 'mock_user_id',
      email: email,
      email_verified: true,
      'custom:driverId': 'DRV001',
    };

    storeTokens(mockTokens);
    storeProfile(mockProfile);

    return { success: true };
  }

  try {
    const response = await fetch(COGNITO_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: config.userPoolClientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const code = data.__type || '';
      if (code.includes('NotAuthorized')) return { success: false, error: 'Incorrect email or password.' };
      if (code.includes('UserNotFound')) return { success: false, error: 'No account found with this email.' };
      if (code.includes('UserNotConfirmed')) return { success: false, error: 'Account not confirmed. Please check your email.' };
      return { success: false, error: data.message || 'Login failed. Please try again.' };
    }

    const result = data.AuthenticationResult;
    if (!result?.AccessToken || !result?.IdToken) {
      return { success: false, error: 'Failed to retrieve session tokens.' };
    }

    const idPayload = decodeJwtPayload(result.IdToken);

    const authTokens: AuthTokens = {
      accessToken: result.AccessToken,
      idToken: result.IdToken,
      refreshToken: result.RefreshToken || '',
      expiresIn: result.ExpiresIn || 3600,
    };

    const userEmail = String(idPayload?.email || idPayload?.['cognito:username'] || idPayload?.username || email);
    const sub = String(idPayload?.sub || '');

    const profile: UserProfile = {
      sub,
      email: userEmail,
      email_verified: true,
      'custom:driverId': userEmail,
    };

    storeTokens(authTokens);
    storeProfile(profile);

    return { success: true };
  } catch (error: any) {
    console.error('Login error:', error);
    return { success: false, error: error.message || 'Login failed. Please try again.' };
  }
}

/**
 * Logout user
 */
export async function logout() {
  // Revoke token via Cognito if we have a refresh token
  const tokens = getStoredTokens();
  if (tokens?.refreshToken) {
    try {
      await fetch(COGNITO_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.RevokeToken',
        },
        body: JSON.stringify({
          ClientId: config.userPoolClientId,
          Token: tokens.refreshToken,
        }),
      });
    } catch {
      // Ignore revocation errors
    }
  }
  clearAuth();
  window.location.href = withBasePath('/');
}

/**
 * Get current user profile
 */
export function getCurrentUser(): UserProfile | null {
  const profileJson = localStorage.getItem(PROFILE_STORAGE_KEY);
  if (!profileJson) return null;

  try {
    return JSON.parse(profileJson);
  } catch {
    return null;
  }
}

/**
 * Get auth token for API requests
 */
export function getAuthToken(): string | null {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  const accessPayload = decodeJwtPayload(tokens.accessToken);
  const idPayload = decodeJwtPayload(tokens.idToken);

  // Prefer the token that is most likely to be accepted by DriverAPI and contains user identity claims.
  if (idPayload?.token_use === 'id' || idPayload?.email || idPayload?.['cognito:username']) {
    return tokens.idToken;
  }

  if (accessPayload?.token_use === 'access') {
    return tokens.accessToken;
  }

  return tokens.idToken || tokens.accessToken || null;
}

/**
 * Make authenticated API request
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  // MOCK MODE: Return mock data for dashboard
  if (config.mockMode) {
    console.log(`🎭 Mock Mode: Skipping API request to ${endpoint}`);
    return {} as T;
  }

  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Token expired or invalid
    clearAuth();
    window.location.href = withBasePath('/');
    throw new Error('Authentication expired');
  }

  if (!response.ok) {
    let errorMessage = 'Request failed';
    try {
      const error = await response.json();
      const baseMessage = error.message || error.error?.message || errorMessage;
      const detailMessage = error.error?.error || error.detail || error.details?.error;
      errorMessage = detailMessage && detailMessage !== baseMessage
        ? `${baseMessage}: ${detailMessage}`
        : baseMessage;
    } catch {
      // Not JSON — ignore
    }
    throw new Error(errorMessage);
  }

  const json = await response.json();
  return json?.data ?? json;
}

// ==================== PRIVATE HELPERS ====================

export function getStoredTokens(): AuthTokens | null {
  const tokensJson = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!tokensJson) return null;

  try {
    return JSON.parse(tokensJson);
  } catch {
    return null;
  }
}

function storeTokens(tokens: AuthTokens) {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(tokens));
}

function storeProfile(profile: UserProfile) {
  localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
}

function clearAuth() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(PROFILE_STORAGE_KEY);
}

function isTokenExpired(tokens: AuthTokens): boolean {
  // Decode the JWT exp claim directly (fixes the bug where expiresIn was added to now)
  try {
    const payload = decodeJwtPayload(tokens.accessToken);
    if (payload?.exp) {
      // Expire 5 minutes early for buffer
      return Date.now() >= (payload.exp * 1000) - 5 * 60 * 1000;
    }
  } catch {
    // Fall through to expiresIn fallback
  }
  // If we can't decode, assume expired to force refresh
  return true;
}

async function refreshTokens(): Promise<void> {
  const tokens = getStoredTokens();
  if (!tokens?.refreshToken) throw new Error('No refresh token');

  const response = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: config.userPoolClientId,
      AuthParameters: {
        REFRESH_TOKEN: tokens.refreshToken,
      },
    }),
  });

  if (!response.ok) throw new Error('Token refresh failed');

  const data = await response.json();
  const result = data.AuthenticationResult;
  if (!result?.AccessToken) throw new Error('No access token in refresh response');

  storeTokens({
    accessToken: result.AccessToken,
    idToken: result.IdToken || tokens.idToken,
    refreshToken: tokens.refreshToken, // refresh token not rotated by default
    expiresIn: result.ExpiresIn || 3600,
  });
}

/**
 * Decode JWT payload (base64url) without verifying signature
 */
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 → JSON
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json);
  } catch {
    return null;
  }
}
